import { Injectable, isDevMode } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { EventsDaoService } from 'src/app/dao/events-dao-service/events-dao.service';
import { CenterAlert } from 'src/app/models/center-model/center-alert.model';
import { CenterAlertContact } from 'src/app/models/center-model/center-alert-contact.model';
import { CompanyMailConfig } from 'src/app/models/company-data-model/company-mail-config.model';
import { CenterAlertApplication } from 'src/app/models/center-model/center-alert-application.model';
import { UserService } from 'src/app/services/user-service/user.service';
import { tap } from 'rxjs/internal/operators/tap';
import { map } from 'rxjs/internal/operators/map';
import { UserStorageService } from '../user-storage-service/user-storage.service';
import { CenterAlertIotDevice } from 'src/app/models/center-model/center-alert-iot-device.model';
import { CentersCentersDaoService } from 'src/app/dao/centers-dao-services/centers-centers-dao-service/centers-centers-dao.service';

@Injectable({
  providedIn: 'root'
})
export class EventsService {

  accessToken = 'CEN';
  refreshAlertListener = new Subject<any>();
  refreshAlertsListener = new Subject<any>();
  searchAlertsListener = new Subject<any>();
  filterAlertsListener = new Subject<any>();
  toggleEventsDispositionListener = new Subject<any>();

  constructor(private eventsDaoService: EventsDaoService,
              private centerDaoService: CentersCentersDaoService,
              private userStorageService: UserStorageService,
              private userService: UserService) { }

  public getAlerts() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.getAlerts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const alerts = new Array<CenterAlert>();
          o['data']['centerAlertList'].forEach(
            (a) => {
              const alert = new CenterAlert();
              const centerAlertContacts = new Array<CenterAlertContact>();
              const companyMailConfig = new CompanyMailConfig();
              a['centerAlertContacts'].forEach(
                (c) => {
                  const centerAlertContact = new CenterAlertContact();
                  Object.assign(centerAlertContact, c);
                  centerAlertContacts.push(centerAlertContact);
                }
              );
              Object.assign(alert, a);
              Object.assign(companyMailConfig, a['companyMailConfig']);
              alert.CenterAlertContacts = centerAlertContacts;
              alert.CompanyMailConfig = companyMailConfig;
              alerts.push(alert);
            }
          );
          return alerts;
        })
      );
    }
    return EMPTY;
  }

  public getAlertsCounter() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.getAlertsCounter(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            availableAlerts: o['data']['counters']['nbAvailables'],
            unavailableAlerts: o['data']['counters']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }

  public getAlertInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.getAlertInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const alert = new CenterAlert();
          const centerAlertApplications = new Array<CenterAlertApplication>();
          const centerAlertContacts = new Array<CenterAlertContact>();
          const IotDevices = new Array<CenterAlertIotDevice>();
          const companyMailConfig = new CompanyMailConfig();
          o['data']['centerAlert']['centerAlertApplications'].forEach(
            (a) => {
              const centerAlertApplication = new CenterAlertApplication();
              Object.assign(centerAlertApplication, a);
              centerAlertApplications.push(centerAlertApplication);
            }
          );
          o['data']['centerAlert']['centerAlertContacts'].forEach(
            (c) => {
              const centerAlertContact = new CenterAlertContact();
              Object.assign(centerAlertContact, c);
              centerAlertContacts.push(centerAlertContact);
            }
          );
          o['data']['centerAlert']['iotDevices'].forEach(
            (c) => {
              const iotDevice = new CenterAlertIotDevice();
              Object.assign(iotDevice, c);
              IotDevices.push(iotDevice);
            }
          );
          Object.assign(companyMailConfig, o['data']['centerAlert']['companyMailConfig']);
          Object.assign(alert, o['data']['centerAlert']);
          alert.CompanyMailConfig = companyMailConfig;
          alert.CenterAlertApplications = centerAlertApplications;
          alert.CenterAlertContacts = centerAlertContacts;
          alert.IotDevices = IotDevices;
          return alert;
        })
      );
    }
    return EMPTY;
  }

  public getAlertApplications(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.getAlertApplications(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const applications = new Array<CenterAlertApplication>();
          o['data']['centersApplicationList'].forEach(
            (a) => {
              const application = new CenterAlertApplication();
              Object.assign(application, a);
              applications.push(application);
            }
          );
          return applications;
        })
      );
    }
    return EMPTY;
  }

  public getCompanyMailProviders(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.getCompanyMailProviders(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const mailProviders = new Array<CompanyMailConfig>();
          o['data']['companyMailConfigList'].forEach(
            (c) => {
              const mailProvider = new CompanyMailConfig();
              Object.assign(mailProvider, c);
              mailProviders.push(mailProvider);
            }
          );
          return mailProviders;
        })
      );
    }
    return EMPTY;
}

  public addNewAlert(active, name, selectedCenter, centerAlertDeviceList, centerAlertContacts, applications, iotDevices, alertOccupationItems, selectedMailProvider) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.addNewAlert(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, name, selectedCenter, centerAlertDeviceList, centerAlertContacts, applications, iotDevices, alertOccupationItems, selectedMailProvider).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertStatus(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertInfo(internalId, active, name, selectedCenter) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, name, selectedCenter).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertContacts(internalId, centerAlertContacts) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertContacts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerAlertContacts).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    } return EMPTY;
  }

  public updateAlertAlertTypes(internalId, centerAlertDeviceList) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertAlertTypes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerAlertDeviceList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertApplications(internalId, centerAlertApplications) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertApplications(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerAlertApplications).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertIotDevices(internalId, iotDevices) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertIotDevices(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, iotDevices).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertCapacityList(internalId, centerAlertCapacityList) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertCapacityList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerAlertCapacityList).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateAlertMailConfigProvider(internalId, mailConfigInternalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.updateAlertMailConfigProvider(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, { internalId: mailConfigInternalId }).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteAlert(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.eventsDaoService.deleteAlert(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public refreshAlerts() {
    this.refreshAlertsListener.next();
  }

  public refreshAlertsListen() {
    return this.refreshAlertsListener.asObservable();
  }

  public refreshAlert() {
    this.refreshAlertListener.next();
  }

  public refreshAlertListen() {
    return this.refreshAlertListener.asObservable();
  }

  public searchAlerts(filterKey) {
    this.searchAlertsListener.next(filterKey);
  }

  public searchAlertsListen() {
    return this.searchAlertsListener.asObservable();
  }

  public filterAlerts(opts) {
    this.filterAlertsListener.next(opts);
  }

  public filterAlertsListen() {
    return this.filterAlertsListener.asObservable();
  }
  public toggleEventsDisposition(viewDis) {
    this.toggleEventsDispositionListener.next(viewDis);
  }
  public toggleEventsDispositionListen() {
    return this.toggleEventsDispositionListener.asObservable();
  }
}
